import React from 'react';
import {Heading, HeadingLevel, HeadingVariant, HeadingWeight, HeadingColor} from '../typography/heading';
import {Text, TextVariant, TextColor} from '../typography/text';
import {Button, ButtonVariant, ButtonSize} from '../button';
import {Spacer} from '..';
import {OpportunityStatus} from '../../models/opportunity';

interface EmptyStateProps {
  icon?: string;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
  className?: string;
  type?: OpportunityStatus;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionIcon,
  className = '',
  type,
}) => {
  const messages = {
    [OpportunityStatus.PUBLISHED]: {
      title: 'No active opportunities',
      description: "You haven't published any opportunities yet. Create one to start attracting candidates.",
      button: 'Create an Opportunity',
    },
    [OpportunityStatus.DRAFT]: {
      title: 'No drafts saved yet',
      description: 'Your drafted opportunities will appear here. Start drafting a new one to review later.',
      button: 'Create a Draft',
    },
    [OpportunityStatus.CLOSED]: {
      title: 'No closed opportunities',
      description: 'Closed opportunities will be shown here once you archive or complete them.',
      button: 'View Active Opportunities',
    },
  };

  const finalTitle = type && type in messages ? messages[type as keyof typeof messages].title : title;
  const finalDescription = type && type in messages ? messages[type as keyof typeof messages].description : description;
  const finalActionLabel = type && type in messages ? messages[type as keyof typeof messages].button : actionLabel;

  const containerClasses = ['flex flex-col items-center justify-center text-center flex-1', className].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {icon && <img src={icon} alt={finalTitle} className="w-20 h-20 mx-auto" />}
      <Spacer />
      <Heading
        level={HeadingLevel.H2}
        variant={HeadingVariant.PAGE_TITLE}
        weight={HeadingWeight.SEMIBOLD}
        color={HeadingColor.PRIMARY}
        className="text-center">
        {finalTitle}
      </Heading>
      <Text variant={TextVariant.BODY_SMALL} color={TextColor.MUTED} className="mb-8 max-w-md text-center">
        {finalDescription}
      </Text>
      <Spacer />
      {finalActionLabel && onAction && type !== OpportunityStatus.CLOSED && (
        <Button variant={ButtonVariant.Primary} size={ButtonSize.SM} onClick={onAction} startIcon={actionIcon}>
          {finalActionLabel}
        </Button>
      )}
    </div>
  );
};
